import { useMemo, useState } from 'react';
import { Sheet } from '../components/Sheet.jsx';
import { Icon } from '../components/Icon.jsx';
import { gueltigeLeistungsart, useCreateLeistung, useLeistungen, useLeistungsarten } from '../hooks/data.js';
import { deleteLeistungen } from '../lib/api.js';
import { remove as removeQueued } from '../lib/outbox.js';
import { addDays, dayLabel, hours, isoDate, kw } from '../lib/format.js';
import { kopierplan, montag } from '../lib/kopieren.js';
import { useToast } from '../state/toast.jsx';

const tag = (l) => String(l.datum ?? '').slice(0, 10);

/**
 * Zeit kopieren: einen Eintrag, einen Tag oder eine Woche (eigene Einträge auf diesem Auftrag).
 * Vorschau mit Häkchen; Zukunft und Doppel sind nicht vorausgewählt. Gebucht wird auf mich, ohne Netz über die
 * Warteschlange. Danach «Rückgängig» für alles, was gerade kopiert wurde.
 */
export function KopierSheet({ nr, quelle, onClose }) {
  // quelle: { art: 'eintrag', eintrag } | { art: 'tag', datum }
  const heute = isoDate();
  const leistungen = useLeistungen(nr);
  const arten = useLeistungsarten();
  const create = useCreateLeistung(nr);
  const toast = useToast();
  const alle = useMemo(() => leistungen.data ?? [], [leistungen.data]);
  const meine = useMemo(() => alle.filter((l) => l.meine), [alle]);

  const [modus, setModus] = useState(quelle.art === 'eintrag' ? 'eintrag' : 'tag');
  const quellDatum = quelle.art === 'eintrag' ? tag(quelle.eintrag) : quelle.datum;
  const [zielTag, setZielTag] = useState(quelle.art === 'eintrag' || quellDatum !== heute ? heute : addDays(heute, 1));
  const [zielWoche, setZielWoche] = useState(() => (montag(quellDatum) === montag(heute) ? addDays(montag(heute), 7) : montag(heute)));
  const [wahl, setWahl] = useState(() => new Map()); // Index → bewusst an/abgewählt (überschreibt den Vorschlag)
  const [busy, setBusy] = useState(false);

  const eintraege = useMemo(() => {
    if (modus === 'eintrag') return [quelle.eintrag];
    if (modus === 'tag') return meine.filter((l) => tag(l) === quellDatum);
    const mo = montag(quellDatum);
    return meine.filter((l) => tag(l) >= mo && tag(l) <= addDays(mo, 6));
  }, [modus, quelle, meine, quellDatum]);

  const plan = useMemo(() => kopierplan(eintraege, {
    modus: modus === 'woche' ? 'woche' : 'tag',
    ziel: modus === 'woche' ? zielWoche : zielTag,
    heute,
    bestehend: meine,
    doppelOk: modus === 'eintrag',
  }).map((p, i) => ({ ...p, an: wahl.has(i) ? wahl.get(i) : p.an })), [eintraege, modus, zielWoche, zielTag, heute, meine, wahl]);

  // Leistungsart der Kopie: die des Originals, sonst eine gültige aus Exxas (wird in der Vorschau angezeigt)
  const artTitel = (ref) => arten.data?.find((x) => x.id === gueltigeLeistungsart(arten.data, ref))?.titel ?? '';
  const gewaehlt = plan.filter((p) => p.an);
  const summe = gewaehlt.reduce((s, p) => s + p.quelle.stunden, 0);
  const umschalten = (i, p) => setWahl((m) => new Map(m).set(i, !p.an));
  // Neue Auswahl oder neues Ziel: wieder vom Vorschlag ausgehen (Zeilen und Zukunft/Doppel ändern sich)
  const neuerModus = (m) => { setModus(m); setWahl(new Map()); };
  const neuesZielTag = (d) => { setZielTag(d); setWahl(new Map()); };
  const neuesZielWoche = (d) => { setZielWoche(d); setWahl(new Map()); };

  const buchen = async () => {
    setBusy(true);
    const erstellt = [];
    const vorgemerkt = [];
    let fehler = null;
    for (const p of gewaehlt) {
      try {
        const leistungsartId = gueltigeLeistungsart(arten.data, p.quelle.refArtikel);
        const r = await create.mutateAsync({ datum: p.datum, stunden: p.quelle.stunden, beschreibung: p.quelle.beschreibung ?? '', leistungsartId, label: `${hours(p.quelle.stunden)} ${p.quelle.beschreibung || 'Arbeitszeit'} (${dayLabel(p.datum)})` });
        if (r.queued) vorgemerkt.push(r.id);
        else erstellt.push(...(r.res?.erstellt ?? []).map((x) => x.id).filter(Boolean));
      } catch (err) { fehler = err.message; break; }
    }
    setBusy(false);
    const n = erstellt.length + vorgemerkt.length;
    if (fehler && !n) { toast({ text: `Nicht kopiert: ${fehler}`, error: true }); return; }
    toast({
      text: `${n} ${n === 1 ? 'Eintrag' : 'Einträge'} kopiert${vorgemerkt.length ? ' (auf dem Gerät gespeichert, wird übertragen)' : ''}${fehler ? `. Rest abgebrochen: ${fehler}` : ''}`,
      error: !!fehler,
      long: true,
      undo: async () => {
        vorgemerkt.forEach((id) => removeQueued(id));
        const f = erstellt.length ? await deleteLeistungen(nr, erstellt) : [];
        leistungen.refetch();
        toast(f.length ? { text: `Nicht alles zurückgenommen: ${f[0]}`, error: true } : { text: 'Kopie zurückgenommen' });
      },
    });
    onClose();
  };

  const titel = modus === 'eintrag' ? 'Eintrag kopieren' : modus === 'tag' ? 'Tag kopieren' : 'Woche kopieren';
  return (
    <Sheet title={titel} onClose={onClose}>
      <div className="segment" role="group" aria-label="Was kopieren">
        {quelle.art === 'eintrag' && <button type="button" aria-pressed={modus === 'eintrag'} className={modus === 'eintrag' ? 'on' : ''} onClick={() => neuerModus('eintrag')}>Eintrag</button>}
        <button type="button" aria-pressed={modus === 'tag'} className={modus === 'tag' ? 'on' : ''} onClick={() => neuerModus('tag')}>Tag</button>
        <button type="button" aria-pressed={modus === 'woche'} className={modus === 'woche' ? 'on' : ''} onClick={() => neuerModus('woche')}>Woche</button>
      </div>

      {modus === 'woche' ? (
        <div className="stack" style={{ gap: 8 }}>
          <span className="label">Von KW {kw(quellDatum)} in die Woche</span>
          <div className="wrapchips">
            {[[montag(heute), `Diese Woche (KW ${kw(heute)})`], [addDays(montag(heute), 7), `Nächste Woche (KW ${kw(addDays(heute, 7))})`]].map(([mo, label]) => (
              <button key={mo} type="button" className={`chip${zielWoche === mo ? ' on' : ''}`} aria-pressed={zielWoche === mo} onClick={() => neuesZielWoche(mo)}>{label}</button>
            ))}
          </div>
        </div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          <span className="label">Von {dayLabel(quellDatum)} auf</span>
          <div className="wrapchips">
            {[[heute, 'Heute'], [addDays(heute, -1), 'Gestern'], [addDays(heute, 1), 'Morgen']].map(([d, label]) => (
              <button key={d} type="button" className={`chip${zielTag === d ? ' on' : ''}`} aria-pressed={zielTag === d} onClick={() => neuesZielTag(d)}>{label}</button>
            ))}
          </div>
          <label className="field">
            <span className="vh">Anderes Datum</span>
            <input className="input" type="date" value={zielTag} onChange={(e) => e.target.value && neuesZielTag(e.target.value)} />
          </label>
        </div>
      )}

      <div className="card list" style={{ padding: '2px 14px', background: 'var(--bg)' }}>
        {plan.length === 0 && <p className="sub" style={{ padding: '12px 0' }}>Keine eigenen Einträge zum Kopieren.</p>}
        {plan.map((p, i) => (
          <label key={`${p.quelle.id}-${i}`} className="listrow" style={{ cursor: 'pointer', minHeight: 'var(--tap-lg)' }}>
            <input type="checkbox" checked={p.an} onChange={() => umschalten(i, p)} style={{ width: 24, height: 24, accentColor: 'var(--accent)', flexShrink: 0 }} />
            <span className="stack" style={{ flex: 1, gap: 2 }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>{p.quelle.beschreibung || p.quelle.leistungsart || 'Arbeitszeit'}</span>
              <span className="sub">
                {dayLabel(p.datum)}
                {artTitel(p.quelle.refArtikel) && `, ${artTitel(p.quelle.refArtikel)}`}
                {p.zukunft && ', liegt in der Zukunft'}
                {p.doppelt && ', steht dort schon so'}
              </span>
            </span>
            <span className="tn" style={{ fontSize: 15, fontWeight: 800 }}>{hours(p.quelle.stunden)}</span>
          </label>
        ))}
      </div>

      <button className="btn lg block" onClick={buchen} disabled={busy || !gewaehlt.length || !arten.data}>
        <Icon name="check" />
        {busy ? 'Wird gebucht …' : gewaehlt.length ? `${gewaehlt.length} ${gewaehlt.length === 1 ? 'Eintrag' : 'Einträge'} buchen (${hours(summe)})` : 'Nichts ausgewählt'}
      </button>
    </Sheet>
  );
}
